/**
 * ShareParlayModal - Modal for sharing a parlay with other users
 * Allows selecting users from followers/following and sharing pending parlays
 */

import { type ShareableUser, useSharing } from '@hooks/useSharing';
import { shareOutcome } from '@parlay-picker/shared/hooks';
import { Check, Search, Send, Users, X } from 'lucide-react';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import LoadingSpinner from './LoadingSpinner';

// =============================================================================
// Types
// =============================================================================

interface ShareParlayModalProps {
  parlayId: number;
  parlayName: string;
  onClose: () => void;
  onShareSuccess?: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

function getRelationshipBadge(relationship: ShareableUser['relationship']): {
  label: string;
  className: string;
} {
  switch (relationship) {
    case 'friend':
      return {
        label: 'Friend',
        className: 'bg-theme-success/10 text-theme-success border-theme-success/30',
      };
    case 'following':
      return {
        label: 'Following',
        className: 'bg-theme-primary/10 text-theme-primary border-theme-primary/30',
      };
    case 'follower':
      return {
        label: 'Follower',
        className: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      };
  }
}

// =============================================================================
// Component
// =============================================================================

export function ShareParlayModal({
  parlayId,
  parlayName,
  onClose,
  onShareSuccess,
}: ShareParlayModalProps): ReactElement {
  const {
    shareableUsers,
    fetchShareableUsers,
    shareParlay,
    shareableUsersLoading,
    shareError,
    shareableUsersError,
  } = useSharing();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [sharing, setSharing] = useState(false);
  // A 200 that reached nobody; a refused request leaves the server's reason in shareError
  const [noRecipientError, setNoRecipientError] = useState<string | null>(null);
  const displayedError = noRecipientError ?? shareError;
  const [shareSuccessMessage, setShareSuccessMessage] = useState<string | null>(null);

  // Fetch shareable users on mount
  useEffect(() => {
    fetchShareableUsers();
  }, [fetchShareableUsers]);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return shareableUsers;

    const query = searchQuery.toLowerCase();
    return shareableUsers.filter((user) => user.displayName.toLowerCase().includes(query));
  }, [shareableUsers, searchQuery]);

  // Toggle user selection
  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Select/deselect all filtered users
  const toggleAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map((u) => u.id)));
    }
  };

  // Handle share action
  const handleShare = async () => {
    if (selectedUserIds.size === 0) return;

    setSharing(true);
    setNoRecipientError(null);

    const result = await shareParlay(parlayId, Array.from(selectedUserIds));

    if (result) {
      const outcome = shareOutcome(result);
      if (!outcome.ok) {
        setNoRecipientError(outcome.error);
      } else {
        setShareSuccessMessage(outcome.message);
        onShareSuccess?.();
        // Close after brief delay to show success
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    }

    setSharing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md max-h-[85vh] mx-4 bg-theme-surface rounded-xl border border-theme-border overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-theme-border shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-100">Share Parlay</h2>
            <p className="text-sm text-theme-text-secondary mt-0.5 truncate max-w-[280px]">
              {parlayName || 'Untitled Parlay'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-theme-text-secondary hover:text-gray-200 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success State */}
        {shareSuccessMessage && (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <div className="w-16 h-16 rounded-full bg-theme-success/20 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-theme-success" />
            </div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Parlay Shared!</h3>
            <p className="text-sm text-theme-text-secondary">{shareSuccessMessage}</p>
          </div>
        )}

        {/* Loading State */}
        {!shareSuccessMessage && shareableUsersLoading && shareableUsers.length === 0 && (
          <div className="flex-1 flex items-center justify-center p-8">
            <LoadingSpinner />
          </div>
        )}

        {/* No users: the load's error when it failed, the empty state only when it worked */}
        {!shareSuccessMessage &&
          !shareableUsersLoading &&
          shareableUsers.length === 0 &&
          (shareableUsersError ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <p className="text-sm text-theme-danger">{shareableUsersError}</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-theme-surface flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-theme-text-secondary mb-2">
                No one to share with
              </h3>
              <p className="text-sm text-theme-text-tertiary max-w-xs">
                Follow other users or get followers to share your parlays with them.
              </p>
            </div>
          ))}

        {/* Main Content */}
        {!shareSuccessMessage && shareableUsers.length > 0 && (
          <>
            {/* Search */}
            <div className="p-4 border-b border-gray-800 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-text-secondary" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-theme-surface border border-theme-border rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-theme-primary"
                />
              </div>

              {/* Select All Toggle */}
              {filteredUsers.length > 1 && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="mt-2 text-xs text-theme-primary hover:text-blue-300 transition-colors"
                >
                  {selectedUserIds.size === filteredUsers.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>

            {/* User List */}
            <div className="flex-1 overflow-y-auto p-2">
              {filteredUsers.length === 0 ? (
                <div className="p-4 text-center text-sm text-theme-text-tertiary">
                  No users match your search
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUserIds.has(user.id);
                    const badge = getRelationshipBadge(user.relationship);

                    return (
                      <button
                        type="button"
                        key={user.id}
                        onClick={() => toggleUser(user.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                          isSelected
                            ? 'bg-theme-primary/10 border border-theme-primary/30'
                            : 'bg-theme-surface/50 border border-transparent hover:bg-theme-surface'
                        }`}
                      >
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                            isSelected
                              ? 'bg-theme-primary-fill border-theme-primary'
                              : 'border-theme-border'
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-theme-on-primary-fill" />}
                        </div>

                        {/* Avatar (initials) */}
                        <div className="w-10 h-10 rounded-full bg-theme-surface-hover flex items-center justify-center">
                          <span className="text-sm font-medium text-theme-text-secondary">
                            {user.displayName.charAt(0).toUpperCase()}
                          </span>
                        </div>

                        {/* User Info */}
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-100 truncate">
                              {user.displayName}
                            </span>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded border ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Error Display */}
            {displayedError && (
              <div className="px-4 py-2 bg-theme-danger/10 border-t border-theme-danger/20">
                <p className="text-sm text-theme-danger">{displayedError}</p>
              </div>
            )}

            {/* Footer */}
            <div className="p-4 border-t border-theme-border shrink-0">
              <button
                type="button"
                onClick={handleShare}
                disabled={selectedUserIds.size === 0 || sharing}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  selectedUserIds.size > 0 && !sharing
                    ? 'bg-theme-primary-fill hover:bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'bg-theme-surface-hover text-theme-text-secondary cursor-not-allowed'
                }`}
              >
                {sharing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Sharing...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>
                      Share with {selectedUserIds.size}{' '}
                      {selectedUserIds.size === 1 ? 'user' : 'users'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
